/**
 * storeFilters — pure filter / counter helpers for ManagePlatform.
 * Extracted from ManagePlatform.js so the page component stays presentational.
 *
 * Conventions
 *  - `pendingStores` = stores with statuses {pending, pending_review, needs_edit,
 *    modification_required, edited, resubmitted}
 *  - `stores`        = approved + stopped + rejected stores (post-pending lifecycle)
 *  - All helpers are pure; no side effects, no React.
 */

const PENDING_STATUSES = new Set([
  'pending', 'pending_review',
  'needs_edit', 'modification_required',
  'edited', 'resubmitted',
]);

const isPersonal = (s) => (
  !s.business_type || s.business_type === 'individual' || s.business_type === 'personal'
);
const isCompany = (s) => s.business_type === 'company';

const matchBusinessType = (s, businessType) => {
  if (businessType === 'personal') return isPersonal(s);
  if (businessType === 'company') return isCompany(s);
  return true;
};

// ---------------------------------------------------------------------------
// Pending / store-request side
// ---------------------------------------------------------------------------
export const filterPendingStoresForRequest = ({
  pendingStores = [],
  storeBusinessType = null,
  statusFilter = 'new',
  typeFilter = 'all',
  dateFrom = '',
  dateTo = '',
}) => {
  let filtered = pendingStores.filter((s) => {
    if (!matchBusinessType(s, storeBusinessType)) return false;
    if (statusFilter === 'new') {
      return s.status === 'pending' || s.status === 'pending_review';
    }
    if (statusFilter === 'needs_edit') {
      return s.status === 'needs_edit' || s.status === 'modification_required';
    }
    if (statusFilter === 'edited') {
      return s.status === 'edited' || s.status === 'resubmitted';
    }
    return false;
  });

  if (typeFilter && typeFilter !== 'all') {
    filtered = filtered.filter((s) => s.type === typeFilter);
  }

  if (dateFrom) {
    const fromDate = new Date(dateFrom);
    filtered = filtered.filter((s) => new Date(s.created_at) >= fromDate);
  }
  if (dateTo) {
    const toDate = new Date(dateTo);
    toDate.setHours(23, 59, 59, 999);
    filtered = filtered.filter((s) => new Date(s.created_at) <= toDate);
  }

  return filtered;
};

export const getStoreRequestCounts = ({
  pendingStores = [],
  storeBusinessType = null,
} = {}) => {
  const scoped = pendingStores.filter((s) => matchBusinessType(s, storeBusinessType));
  return {
    newCount: scoped.filter((s) => s.status === 'pending' || s.status === 'pending_review').length,
    needsEditCount: scoped.filter((s) => s.status === 'needs_edit' || s.status === 'modification_required').length,
    editedCount: scoped.filter((s) => s.status === 'edited' || s.status === 'resubmitted').length,
  };
};

export const getBusinessTypeCounts = ({ pendingStores = [] } = {}) => {
  const isPendingStatus = (s) => PENDING_STATUSES.has(s.status);
  return {
    personalCount: pendingStores.filter((s) => isPersonal(s) && isPendingStatus(s)).length,
    companyCount: pendingStores.filter((s) => isCompany(s) && isPendingStatus(s)).length,
  };
};

// ---------------------------------------------------------------------------
// Manage stores side (active / stopped / rejected)
// ---------------------------------------------------------------------------
const VISIBLE_MANAGE_STATUSES = new Set(['active', 'approved', 'rejected', 'stopped']);

export const filterStoresForManage = ({
  stores = [],
  storeManageType = null,
  storeListFilter = 'active',
} = {}) => {
  let filtered = stores.filter((s) => VISIBLE_MANAGE_STATUSES.has(s.status));

  if (storeManageType === 'personal') {
    filtered = filtered.filter(isPersonal);
  } else if (storeManageType === 'company') {
    filtered = filtered.filter(isCompany);
  }

  if (storeListFilter === 'active') {
    filtered = filtered.filter((s) => s.status === 'active' || s.status === 'approved');
  } else if (storeListFilter === 'stopped') {
    filtered = filtered.filter((s) => s.status === 'stopped');
  } else if (storeListFilter === 'rejected') {
    filtered = filtered.filter((s) => s.status === 'rejected');
  }
  return filtered;
};

export const getStoreManageCounts = ({ stores = [] } = {}) => {
  const visible = stores.filter((s) => VISIBLE_MANAGE_STATUSES.has(s.status));
  return {
    personalCount: visible.filter(isPersonal).length,
    companyCount: visible.filter(isCompany).length,
  };
};
